"use client";

import React from "react";
import { Button, Container, Typography, Badge } from "@/components/design-system";
import { CATEGORIES } from "@/constants/categories";
import Link from "next/link";
import { ArrowRight, Sparkles } from "lucide-react";
import { useLocaleNavigation } from "@/lib/navigation";
import { useLocale, useTranslations } from "next-intl";

export default function HomeCategories() {
    const { getNavigationPath } = useLocaleNavigation();
    const locale = useLocale();
    const t = useTranslations('homepage.homeCategories');

    return (
        <section className="py-24 bg-gradient-to-b from-background to-muted/20">
            <Container>
                <div className="max-w-5xl mx-auto text-center mb-20">
                    <div className="animate-in fade-in-0 duration-1000">
                        <Badge variant="secondary" className="px-4 py-2 text-sm font-medium mb-6">
                            <Sparkles className="w-4 h-4 mr-2" />
                            {t('badge')}
                        </Badge>
                    </div>
                    
                    <div className="animate-in fade-in-0 duration-1000 delay-200">
                        <Typography variant="h2" className="text-4xl md:text-5xl font-bold mb-6">
                            {t('title')}
                        </Typography>
                        <Typography variant="lead" className="text-xl text-muted-foreground max-w-3xl mx-auto">
                            {t('description')}
                        </Typography>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
                    {Object.values(CATEGORIES).map((category, index) => (
                        <Link 
                            key={category.id} 
                            href={getNavigationPath(category.id as any)} 
                            className="group animate-in fade-in-0 duration-1000"
                            style={{ animationDelay: `${300 + index * 100}ms` }}
                        >
                            <div className="relative h-full p-8 rounded-3xl bg-gradient-to-br from-card to-card/50 border border-border/50 shadow-lg hover:shadow-2xl transition-all duration-500 group-hover:scale-105 group-hover:border-primary/30 overflow-hidden">
                                {/* Background gradient on hover */}
                                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                
                                {/* Icon container */}
                                <div className="relative z-10 mb-6">
                                    <div className="w-16 h-16 bg-gradient-to-br from-primary/20 to-primary/10 rounded-2xl flex items-center justify-center group-hover:bg-gradient-to-br group-hover:from-primary/30 group-hover:to-primary/20 transition-all duration-300">
                                        {React.cloneElement(category.icon, { 
                                            className: 'h-8 w-8 text-primary group-hover:scale-110 transition-transform duration-300' 
                                        } as any)}
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="relative z-10 space-y-4">
                                    <div>
                                        <Typography variant="h3" className="text-2xl font-bold mb-3 group-hover:text-primary transition-colors duration-300">
                                            {category.name[locale]}
                                        </Typography>
                                        <Typography variant="muted" className="text-base leading-relaxed">
                                            {category.description[locale]}
                                        </Typography>
                                    </div>

                                    {/* Generators count */}
                                    <div className="flex items-center gap-2">
                                        <Badge variant="outline" className="text-xs">
                                            {t('generatorsCount', { count: category.generators.length })}
                                        </Badge>
                                    </div>

                                    {/* CTA Button */}
                                    <div className="pt-4">
                                        <Button 
                                            size="sm" 
                                            variant="outline" 
                                            className="w-full group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary transition-all duration-300 cursor-pointer"
                                        >
                                            {t('exploreButton')}
                                            <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
                                        </Button>
                                    </div>
                                </div>

                                {/* Decorative corner element */}
                                <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-primary/20 rounded-tr-lg group-hover:border-primary/40 transition-colors duration-300" />
                            </div>
                        </Link>
                    ))}
                </div>

                {/* Bottom CTA */}
                {/* <div className="text-center mt-16 animate-in fade-in-0 duration-1000 delay-700">
                    <Typography variant="lead" className="text-lg text-muted-foreground mb-6">
                        Can't find what you're looking for?
                    </Typography>
                    <Button size="lg" variant="outline" className="px-8 py-4">
                        Request New Category
                        <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                </div> */}
            </Container>
        </section>
    )
}